import type { Tutorial } from './types';

/**
 * Tutorial content library.  Each tutorial auto-starts once per user (gated
 * by localStorage — see `useTutorial.hasSeen`) the first time the operator
 * visits its owning page, then can be replayed manually via the page's help
 * button.
 *
 * Style guide for new tutorials:
 *  • Lead with an `overview` step: what is this page, why would the operator
 *    open it.  Two or three tight paragraphs, no jargon.  Bullet list of
 *    "what you can do here" concrete verbs.
 *  • Follow with 2-6 spotlight steps that point at concrete controls, only
 *    where an element highlight actually adds clarity (a button that isn't
 *    obvious, a section the operator would otherwise miss).
 *  • Prefer overview steps for workflow explanations that span multiple
 *    controls — spotlights on chained buttons look busy and slow.
 *  • Keep step bodies scannable: short paragraphs, bullets for enumerable
 *    facts.  The card is scrollable but operators bail on walls of text.
 */
export const tutorials: Tutorial[] = [

    // ── Main chart ──────────────────────────────────────────────────────
    {
        id: 'tutorial:map',
        title: 'Map basics',
        description: 'The main tactical chart',
        steps: [
            {
                style: 'overview',
                title: 'Welcome to the chart',
                body:
                    'This is the primary tactical picture. Every asset in your fleet, every AIS contact reporting position, every control measure you or a teammate has drawn, and every plan currently executing — all layered on the same map.\n\n' +
                    'The chart updates live and is also scrubbable: use the time slider at the bottom to rewind and replay the last few hours of history.',
                bullets: [
                    'Pan and zoom with the mouse; the visible bounding box is what the server polls for AIS and contact updates.',
                    'Left-click any marker to open its detail pane on the right.',
                    'Right-click for a context menu — copy coordinates, add an AIS vessel to a plan, sleep a sensor contact, and more.',
                ],
            },
            {
                style: 'spotlight',
                target: '[data-tour="layer-menu-btn"]',
                title: 'Layer visibility',
                body:
                    'Toggle each layer independently. Assets, AIS contacts, contact / asset trails, predictive vectors, plan routes, control measures, the graticule, and a debug overlay of the routing navmesh.',
                bullets: [
                    'Assets can be filtered further by prefix (e.g. "USV-", "AUV-") so you can show one class at a time.',
                    'Tracked AIS targets show their names permanently on the chart — a running list is under the layers menu.',
                ],
                placement: 'right',
            },
            {
                style: 'spotlight',
                target: '[data-tour="contacts-checkbox"]',
                title: 'AIS contacts',
                body:
                    'Vessels reporting AIS positions appear as chevrons rotated to their true heading. The colour encodes the ITU ship-type range: green (cargo/tug/SAR), blue (passenger), orange (fishing), magenta (pleasure/sailing), red (tanker), grey (unknown).',
                bullets: [
                    'Moored or anchored vessels show an anchor glyph instead of a chevron.',
                    'Yellow "clipboard" badge = vessel is on at least one draft plan.',
                    'Right-click a vessel to track its name, add it to a plan, or view its 24-hour synthetic history.',
                ],
                placement: 'right',
            },
            {
                style: 'overview',
                title: 'Draft plans',
                body:
                    'The right-side draft pane is where you assemble a collection plan before executing it. Add tasks (RFCs) by right-clicking AIS vessels or drawing a search area, drag to reorder, pin the ones whose order matters, then optimize.\n\n' +
                    'A "draft" plan does nothing until you hit Execute — before that it is just a scratchpad. You can have several drafts open at once and switch between them from the dropdown at the top of the pane.',
                bullets: [
                    '"Search area" button: click on the map to drop polygon vertices, double-click to finish, name the area, and it becomes a task on the plan.',
                    '"Optimize…" opens a wide detail dialog: per-asset constraints (return-to-origin / loiter / patrol area), execution mode, and up to five alternative solutions with fuel + time breakdowns.',
                ],
            },
            {
                style: 'overview',
                title: 'Drawing control measures',
                body:
                    'The vector-polygon button in the top-left toolbar lets you draw NAIs, air ROZ, sea-surface OPAREAs, and sub-surface OPAREAs directly on the chart.',
                bullets: [
                    'Click to drop each vertex; double-click to finish and name it.',
                    'Right-click any vertex during drawing to remove it — useful for correcting a misplaced click.',
                    'Escape cancels the draw without saving.',
                    'Finished measures show up on the chart and in the Control Measures list on the sidebar.',
                ],
            },
            {
                style: 'overview',
                title: 'Playback and time',
                body:
                    'The bar at the bottom of the map is a time scrubber. Drag to rewind; the "Live" chip indicates you are on the leading edge. "Go to Live" jumps back. The dropdown next to it controls how far back the asset / contact trails render.\n\n' +
                    'Scrub precision auto-increases when you drag slowly, so you can hunt down a specific moment without overshooting.',
            },
            {
                style: 'overview',
                title: 'Focus and follow',
                body:
                    'Clicking an asset marker or an AIS vessel opens its detail pane on the right. Currently-focused targets show a white highlight ring so they stand out at a glance.\n\n' +
                    'When you navigate to the map from the Dashboard or the Tasks page with a specific asset selected, the map enters follow mode — the chosen asset stays centred as you scrub through time. Close the pane or hit the Cancel button on the follow banner to exit.',
            },
        ],
    },

    // ── Plan editing / optimizer ────────────────────────────────────────
    {
        id: 'tutorial:plan',
        title: 'Building a plan',
        description: 'RFCs, assets, constraints, and the optimizer',
        steps: [
            {
                style: 'overview',
                title: 'What a plan is',
                body:
                    'A plan is an ordered set of Requests for Collection (RFCs) assigned to a fleet of assets. Once executed, the system generates STANAG tasks for each asset, sequenced so the fleet visits every RFC in the intended order.\n\n' +
                    'The plan editor is where you compose that set — pick the RFCs, pick the assets, adjust dwell times and search patterns, and then run the optimizer to produce a water-safe route.',
                bullets: [
                    'Each RFC in the plan has its own time-on-station (dwell), standoff, and optionally a search pattern.',
                    'You can pin the head of the list — the optimizer will visit pinned RFCs in the order you set and only re-order the rest.',
                    'Plans stay drafts until you hit Execute; you can have as many drafts as you want.',
                ],
            },
            {
                style: 'overview',
                title: 'Search patterns per RFC',
                body:
                    'Each RFC can specify how the asset should behave on-station: point loiter (default), a parallel-track sweep, an expanding square, a sector search, or an Archimedean spiral.\n\n' +
                    'Pattern parameters (radius, leg spacing, track length, orientation) are editable and a live preview renders inside the RFC dialog. The optimizer folds pattern distance and time into the total fuel + duration estimates, and truncates any part of the pattern that runs across land.',
            },
            {
                style: 'overview',
                title: 'The optimize dialog',
                body:
                    'The wide "Optimize…" detail dialog is where the heavy lifting happens. It has three sections:\n\n' +
                    '1) Execution mode — start each asset from its live position, or from where its currently-executing plan will end.\n\n' +
                    '2) Per-asset post-plan behaviour — what each asset does once it has visited every RFC. Options: idle, return to its origin, loiter at a chosen point, or patrol a chosen area.\n\n' +
                    '3) Alternatives — click Optimize and the multi-asset optimizer produces up to five variants (fastest, longest total observation, fairest, single-asset, half-fleet). Each card shows per-asset transit / on-station / fuel-used / fuel-remaining / end-state so you can compare cost against coverage.',
                bullets: [
                    'Apply on any card promotes that variant to the plan\'s active route.',
                    'Constraints only change the tail of each asset\'s tour — makespan and observation metrics still reflect what the optimizer produced for the RFCs themselves.',
                    'Execution mode is planning-only. You always hit Execute manually.',
                ],
            },
            {
                style: 'overview',
                title: 'Preview vs implementation',
                body:
                    'Below the RFC list you\'ll see a preview of the water-safe route the plan will take, along with total duration and estimated fuel remaining per asset once every leg is complete. This preview updates whenever you change RFC order, asset assignments, dwell times, or pick a different optimizer variant.\n\n' +
                    'The "Implementation" section is the same view once the plan is executing — it is what the assets are actually following.',
            },
            {
                style: 'overview',
                title: 'Executing a plan',
                body:
                    'Execute pushes STANAG tasks to each assigned asset and marks the plan running. The plan then appears on the main chart as a solid polyline for each asset.\n\n' +
                    'Stop halts the plan and clears the pending tasks. Editing an executing plan is intentionally locked — stop it first, edit, then re-execute.',
            },
        ],
    },

    // ── Timeline ────────────────────────────────────────────────────────
    {
        id: 'tutorial:timeline',
        title: 'Timeline',
        description: 'Asset schedules and unavailability windows',
        steps: [
            {
                style: 'overview',
                title: 'What the timeline shows',
                body:
                    'One row per asset, a horizontal timescale across the top. Every plan currently assigned to an asset shows up as a bar in that asset\'s row, and every unavailability window shows up as a hatched blackout block.\n\n' +
                    'It is the "when is my fleet doing what" view — the map is the "where", the timeline is the "when".',
            },
            {
                style: 'overview',
                title: 'Blackout blocks (unavailability)',
                body:
                    'Drag anywhere on empty timeline row to draw a new unavailability window — the asset is treated as offline for that period.\n\n' +
                    'The planner honours these: any asset with an unavailability window overlapping the next 24 hours is skipped by the multi-asset optimizer. This is how you tell the system "USV-03 is in maintenance Wednesday afternoon" without having to manually re-plan around it.',
                bullets: [
                    'Drag the middle of a block to move it.',
                    'Drag either edge to resize.',
                    'Click the × to delete.',
                    'The optional description is shown on hover and in the asset detail page.',
                ],
            },
            {
                style: 'overview',
                title: 'Planned tasks',
                body:
                    'Plan bars in the timeline are read-only — they are a projection of what each asset will be doing when the plan executes. To change them, go to the plan editor for that plan.\n\n' +
                    'Bars are coloured by the plan they belong to, so you can spot conflicts at a glance: two plans overlapping on the same asset row means one is starving the other for time.',
            },
        ],
    },

    // ── RFCs ───────────────────────────────────────────────────────────
    {
        id: 'tutorial:rfc',
        title: 'Requests for Collection',
        description: 'Discrete collection tasking',
        steps: [
            {
                style: 'overview',
                title: 'What an RFC is',
                body:
                    'A Request for Collection is a single tasking: "get me imagery of this vessel", "collect signal in this area", "put a sensor on this point". Every RFC has a target (contact, area, or point), a set of sensors to task, one or more domains (Air / Sea Surface / Sea Sub-surface), and a description of what to collect.\n\n' +
                    'RFCs live in the operation independently of any plan. Once created, one RFC can be added to several plans, and a plan is nothing more than an ordered set of RFCs plus an asset assignment.',
            },
            {
                style: 'overview',
                title: 'Target types',
                body:
                    'Three flavours, picked when you create the RFC:\n\n' +
                    'Contact: an AIS vessel (looked up by MMSI). The RFC target moves with the vessel — the optimizer dead-reckons it forward to the predicted arrival time so the asset gets there when the vessel is still there.\n\n' +
                    'Area: a control measure polygon. The optimizer routes to the polygon centroid; on-station you can attach a search pattern to sweep the interior.\n\n' +
                    'Point: a fixed lat/lon.',
            },
            {
                style: 'overview',
                title: 'Sensors, domains, and info-to-collect',
                body:
                    'Sensors: which physical sensors on the assigned asset should be tasked. Populated from the operation\'s Sensor Types.\n\n' +
                    'Domains: the operating environments an asset must cover to service this RFC — Air, Sea Surface, Sea Sub-surface. Multi-domain RFCs need multi-domain assets or a combination of assets.\n\n' +
                    'Info to collect: free text — visible to operators and to the AI assistant when it summarises a plan.',
            },
            {
                style: 'overview',
                title: 'From RFC to plan',
                body:
                    'Once an RFC exists you can pin it to any draft plan from either this page or the map (right-click a vessel and "Add to plan…"). RFCs stay editable while pinned; changes propagate to every plan that references them.\n\n' +
                    'Deleting an RFC while it is on a plan is a two-step choice: "remove from plan" leaves the RFC in the operation, "delete entirely" wipes it everywhere.',
            },
        ],
    },

    // ── Control Measures ───────────────────────────────────────────────
    {
        id: 'tutorial:controlMeasures',
        title: 'Control Measures',
        description: 'Named geographic constructs',
        steps: [
            {
                style: 'overview',
                title: 'What control measures are',
                body:
                    'Named geographic constructs the fleet is aware of. They come in a few flavours:\n\n' +
                    'NAI (Named Area of Interest), Air ROZ (Restricted Op Zone), Sea-surface OPAREA, Sub-surface OPAREA, Sea Transit Corridor, Point Collection.\n\n' +
                    'Each one carries a set of domain flags (does it apply to air / surface / sub-surface assets?), an optional effective time window (one-off or daily-recurring), and a list of polygon coordinates.',
            },
            {
                style: 'overview',
                title: 'Creating them',
                body:
                    'Two ways in: this page (form-based) or the map (draw the polygon interactively with the vector-polygon toolbar and name it in a small dialog).\n\n' +
                    'Point-collection CMs hold a named list of individual lat/lon points instead of a polygon — used for waypoint sets and beacon lists.',
                bullets: [
                    'Domain flags are what decides whether a plan\'s "authorized areas" list can include this measure — an air-only ROZ can\'t authorize a sub-surface asset.',
                    'Effective-time is enforced only advisorily — the planner does not currently gate scheduling on it, but operators see the badge in the list.',
                ],
            },
            {
                style: 'overview',
                title: 'Using them in a plan',
                body:
                    'Two roles:\n\n' +
                    '1) As RFC targets — an Area RFC points at one control measure, so tasking "collect over the west NAI" is a matter of picking that CM.\n\n' +
                    '2) As authorization areas on a plan — the plan\'s Authorized Areas list tells the optimizer which CMs the fleet is allowed to be inside. Anything outside those is treated as off-limits (except for direct transit).',
            },
        ],
    },

    // ── Contacts ───────────────────────────────────────────────────────
    {
        id: 'tutorial:contacts',
        title: 'Contacts',
        description: 'AIS traffic and sensor contacts',
        steps: [
            {
                style: 'overview',
                title: 'What lives here',
                body:
                    'Every contact currently known to the operation, coming from two sources:\n\n' +
                    '• AIS contacts (chip badge "AIS") — vessels broadcasting their position on channels 87B/88B. Live position, speed, heading, ship type, navigation status.\n\n' +
                    '• Sensor contacts — detections generated by asset sensors (SIGINT, radar, sonar). These carry classification confidence and often lack ID info.',
            },
            {
                style: 'overview',
                title: 'Show on map',
                body:
                    'Every contact has a "show on map" action that jumps the operator to the main chart centred on the contact\'s current position. Use it to visually confirm a track before pulling it into a plan.\n\n' +
                    'From the map, right-clicking an AIS vessel opens the same set of actions — add to plan, track by name, etc.',
            },
            {
                style: 'overview',
                title: 'Filtering and search',
                body:
                    'The list is filterable by contact type, classification, and free-text name. When you have a lot of AIS traffic the filter is the fastest way to narrow to the vessels relevant to your current tasking.',
            },
        ],
    },

    // ── Assets ─────────────────────────────────────────────────────────
    {
        id: 'tutorial:assets',
        title: 'Assets',
        description: 'Fleet management',
        steps: [
            {
                style: 'overview',
                title: 'The fleet',
                body:
                    'Every physical asset the operation can task — USVs, UUVs, UAVs, crewed platforms. Each has an asset type (which supplies cruising speed, fuel capacity, sensor payload) and a live current location if it is reporting one.',
            },
            {
                style: 'overview',
                title: 'Endurance',
                body:
                    'The endurance chip shows the remaining operating time based on the asset\'s current fuel level and its type\'s fuel consumption rate. The optimizer uses this to warn if a plan would exceed endurance before the asset can return.',
                bullets: [
                    'Green = healthy, amber = plan-critical (returning soon), red = below reserve.',
                    'Endurance is populated live from the vehicle telemetry — assets that haven\'t reported recently show a stale badge.',
                ],
            },
            {
                style: 'overview',
                title: 'Unavailability windows',
                body:
                    'Set periods when an asset is offline — maintenance, transit, recovery. The multi-asset optimizer will skip any asset with an overlapping window when producing plan alternatives.\n\n' +
                    'You can set these here on the asset detail page, or interactively by dragging in the Timeline view. Both routes write the same data.',
            },
            {
                style: 'overview',
                title: 'Assigning to plans',
                body:
                    'From the plan editor you pick which assets are in each plan. An asset can be in several plans — the planner won\'t stop you, but the timeline will flag any conflict where two executing plans want the same asset at the same time.',
            },
        ],
    },

    // ── Asset Types ────────────────────────────────────────────────────
    {
        id: 'tutorial:assetTypes',
        title: 'Asset Types',
        description: 'Fleet capability catalogue',
        steps: [
            {
                style: 'overview',
                title: 'Classes of vehicle',
                body:
                    'Every asset in the fleet is an instance of an asset type. The type carries the shared properties — cruising speed, fuel capacity, per-minute fuel consumption at cruise, NATO APP-6D icon code, and which sensor payloads the class supports.\n\n' +
                    'Editing a type here changes the numbers the planner uses for every asset of that class simultaneously — useful for calibration but easy to over-apply, so use with intent.',
            },
            {
                style: 'overview',
                title: 'Cruise speed and fuel',
                body:
                    'Two numbers matter for planning: cruising speed (knots) and fuel consumption at cruise (litres per minute). Together with fuel capacity they set endurance.\n\n' +
                    'These populate the multi-asset optimizer\'s per-asset input — if a class is missing either, the planner falls back to 10 knots and a 1 L/min synthetic burn rate, and results will be an estimate.',
            },
            {
                style: 'overview',
                title: 'Sensors',
                body:
                    'The set of sensor types this class can carry — determines which RFCs the class is eligible to service. Editing here is the way to introduce a new sensor payload option.',
            },
        ],
    },

    // ── Tasks ──────────────────────────────────────────────────────────
    {
        id: 'tutorial:tasks',
        title: 'Tasks',
        description: 'STANAG task queue and history',
        steps: [
            {
                style: 'overview',
                title: 'What tasks are',
                body:
                    'Tasks are the STANAG-4586 orders the system pushes to each asset. When you execute a plan, its RFCs are decomposed into a sequence of tasks per asset — patrol, detect, reposition, deploy — and delivered over the tasking channel.\n\n' +
                    'This page is the queue: every task in flight or queued, plus history of everything that has been executed.',
            },
            {
                style: 'overview',
                title: 'Task types',
                body:
                    'Patrol: fly / drive a route.\n' +
                    'Detect: use a specified sensor to look for a class of target.\n' +
                    'Detect ASW: sub-surface variant with different sensor requirements.\n' +
                    'Reposition: transit to a specific point.\n' +
                    'Deploy: release a payload (buoy, sensor).\n\n' +
                    'Each type has its own required fields — the create-task form only shows those relevant to the chosen type.',
            },
            {
                style: 'overview',
                title: 'Task execution',
                body:
                    'Tasks execute in order per asset. The current task is highlighted; the queue below it are pending. Right-click a task for details, edit before it starts, or delete.\n\n' +
                    'Executing a plan generates a run of tasks; stopping the plan cancels every pending task on it. Individually-created tasks (from the "Create Task" form) live outside plans and have to be managed here.',
            },
        ],
    },

    // ── Users (admin) ─────────────────────────────────────────────────
    {
        id: 'tutorial:users',
        title: 'Users',
        description: 'Operation user directory',
        steps: [
            {
                style: 'overview',
                title: 'Who has access',
                body:
                    'Every operator who has ever signed in and been granted a group appears here. Chorus itself does not create user accounts — that is handled by the operation\'s AWS Cognito user pool. Once a user is created there and signs in, an administrator can assign them to permission groups and they will show up in this list.\n\n' +
                    'Group memberships determine what each user can see and change. Use the "Manage groups & permissions" button in the top-right to edit those on the Permissions page.',
                bullets: [
                    'System groups (e.g. Admin, Operator) are pre-defined and cannot be deleted.',
                    'A user with no groups has read-only access to everything they can already load.',
                    'To revoke access completely, remove all group assignments; the account still exists in Cognito.',
                ],
            },
        ],
    },

    // ── Dashboard ─────────────────────────────────────────────────────
    {
        id: 'tutorial:dashboard',
        title: 'Dashboard',
        description: 'At-a-glance status',
        steps: [
            {
                style: 'overview',
                title: 'What\'s here',
                body:
                    'A single-screen summary of the operation: fleet status, active plans, open RFCs, recent incidents. Use it as your morning check-in — everything that would demand attention is called out.',
                bullets: [
                    'Fleet card: which assets are online, endurance state, any unavailability starting in the next 24 h.',
                    'Plans card: executing / draft counts, deadline-nearing plans.',
                    'RFC card: unassigned RFCs, oldest by information-of-value age.',
                ],
            },
            {
                style: 'overview',
                title: 'Find on map',
                body:
                    'Most cards have a "Find on map" action that deep-links to the main chart with the relevant asset(s) or plan pre-focused. Faster than navigating manually and toggling layers.',
            },
        ],
    },
];

export function findTutorial(id: string): Tutorial | undefined {
    return tutorials.find(t => t.id === id);
}
